import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* =========================
   PKCE UTILITIES
========================= */

// Base64 URL Encode
const base64UrlEncode = (buffer) => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// Generate PKCE Verifier + Challenge
const generatePKCE = async () => {
  const verifier = crypto.randomUUID() + crypto.randomUUID();
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const challenge = base64UrlEncode(digest);
  return { verifier, challenge };
};

function LoginPage() {
  const [isADAuth, setIsADAuth] = useState(true);
  const [formData, setFormData] = useState({
    EmployeeId: "",
    Password: "",
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false); // loader state
  const navigate = useNavigate();

  const TENANTID = "be92c534-7b60-4630-bc01-ec5122548d47";
  const CLIENTID = "b817ad29-534e-495d-b042-6d6e2dc57617";
  const REDIRECT_URI = "https://iassistph.innodata.com";
  const SCOPE = "openid profile email User.Read";

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  /* =========================
     MICROSOFT LOGIN REDIRECT
  ========================= */
  const redirectToMicrosoftLogin = async () => {
    setLoading(true); // start loader
    sessionStorage.removeItem("pkce_verifier");

    const { verifier, challenge } = await generatePKCE();
    sessionStorage.setItem("pkce_verifier", verifier);

    const params = new URLSearchParams({
      client_id: CLIENTID,
      response_type: "code",
      response_mode: "query",
      scope: SCOPE,
      redirect_uri: REDIRECT_URI,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });

    window.location.href =
      `https://login.microsoftonline.com/${TENANTID}/oauth2/v2.0/authorize?` +
      params.toString();
  };

  /* =========================
     NORMAL LOGIN (UNCHANGED)
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // start loader

    console.log(formData);
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Login failed");
      }

      if (response.ok) {
        const loginData = await response.json();
        sessionStorage.setItem("userId", loginData.UserId);
        sessionStorage.setItem("employeeId", loginData.EmployeeId);
        sessionStorage.setItem("firstName", loginData.FirstName);
        sessionStorage.setItem("lastName", loginData.LastName);
        sessionStorage.setItem("email", loginData.EmailAddress);
        sessionStorage.setItem("middleName", loginData.MiddleName);
        sessionStorage.setItem("profilePhoto", loginData.ProfilePhoto);
        sessionStorage.setItem("role", loginData.Role);
        sessionStorage.setItem("facility", loginData.Facility);
        sessionStorage.setItem("level", loginData.Level);
        if(loginData.Role=="Employee"){
          navigate(`/submissions`, { state: loginData });
        }
        else{
          navigate(`/dashboard`, { state: loginData });
        }
      } else {
        const error = await response.json();
        setErrorMessage(error.message || "Login failed");
      }
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false); // stop loader
    }
  };

  /* =========================
     MICROSOFT CALLBACK
  ========================= */
  const handleMicrosoftAuth = async () => {
    setLoading(true); // start loader
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (!code) return;

    const verifier = sessionStorage.getItem("pkce_verifier");
    if (!verifier) {
      setErrorMessage("PKCE verifier missing. Please login again.");
      return;
    }

    try {
      /* 🔑 TOKEN EXCHANGE — FRONTEND */
      const tokenResponse = await fetch(
        `https://login.microsoftonline.com/${TENANTID}/oauth2/v2.0/token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: CLIENTID,
            grant_type: "authorization_code",
            code,
            redirect_uri: REDIRECT_URI,
            code_verifier: verifier,
            scope: SCOPE,
          }),
        }
      );

      const tokenData = await tokenResponse.json();

      if (!tokenData.access_token) {
        throw new Error("Microsoft token exchange failed");
        setLoading(false); // start loader
      }

      /* 👤 GET USER INFO */
      const graphResponse = await fetch(
        "https://graph.microsoft.com/v1.0/me",
        {
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
          },
        }
      );

      const userInfo = await graphResponse.json();
      const email = userInfo.userPrincipalName.replace(/@innodata\.com$/i, "");

      /* 🔐 LOGIN TO YOUR SYSTEM */
      const loginResponse = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          EmployeeId: email,
          Password: "ADlogin@!0987654321",
        }),
      });

      if (!loginResponse.ok) {
        const err = await loginResponse.json();
        throw new Error(err.message || "Internal login failed");
        
        setLoading(false); // stop loader
      }

      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        sessionStorage.setItem("userId", loginData.UserId);
        sessionStorage.setItem("employeeId", loginData.EmployeeId);
        sessionStorage.setItem("firstName", loginData.FirstName);
        sessionStorage.setItem("lastName", loginData.LastName);
        sessionStorage.setItem("email", loginData.EmailAddress);
        sessionStorage.setItem("middleName", loginData.MiddleName);
        sessionStorage.setItem("profilePhoto", loginData.ProfilePhoto);
        sessionStorage.setItem("role", loginData.Role);
        sessionStorage.setItem("facility", loginData.Facility);
        sessionStorage.setItem("level", loginData.Level);
        if(loginData.Role=="Employee"){
          navigate(`/submissions`, { state: loginData });
        }
        else{
          navigate(`/dashboard`, { state: loginData });
        }
      } else {
        const error = await loginResponse.json();
        setErrorMessage(error.message || "Login failed");
        setLoading(false); // stop loader
      }
      // Cleanup
      sessionStorage.removeItem("pkce_verifier");
      window.history.replaceState({}, document.title, "/");

    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false); // stop loader
    }
  };

  useEffect(() => {
    if (window.location.search.includes("code=")) {
      handleMicrosoftAuth();
    }
  }, []);

  /* =========================
     UI (UNCHANGED)
  ========================= */
  return (
    <div className="d-flex min-vh-100">
      <div
        className="d-flex align-items-center justify-content-center text-white"
        style={{
          width: "75%",
          background: "linear-gradient(135deg, #0066cc, #003366)",
        }}
      >
        <img src="./img/logo-innodata-white.png" alt="Logo" style={{ width: 250 }} />
      </div>

      <div className="d-flex align-items-center justify-content-center bg-white" style={{ width: "25%" }}>
        <div className="p-4" style={{ width: "90%" }}>
          <h2>
            Welcome to <b>iASSIST</b>
          </h2>
          <hr />

          {!isADAuth ? (
            <form onSubmit={handleSubmit}>
              <input
                className="form-control mb-3"
                id="EmployeeId"
                placeholder="Employee ID"
                value={formData.EmployeeId}
                onChange={handleChange}
                disabled={loading} // disabled while loading
              />
              <input
                className="form-control mb-3"
                type="password"
                id="Password"
                placeholder="Password"
                value={formData.Password}
                onChange={handleChange}
                disabled={loading} // disabled while loading
              />
              <button className="btn btn-primary" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"} {/* loader text */}
              </button>
            </form>
          ) : (
            <button
              className="btn btn-primary rounded-pill px-4 py-2"
              onClick={redirectToMicrosoftLogin}
              disabled={loading} // disabled while loading
            >
              {loading ? "Redirecting..." : "Login via Microsoft"} {/* loader text */}
            </button>
          )}

          {errorMessage && (
            <div className="alert alert-danger mt-3">{errorMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;